import { pollUntilReady } from './poll';

/**
 * Источник «стерео-звонок»: запись с АТС разбирает bit-dual-channel-v3 по
 * каналам — оператор и клиент в них уже разведены, диаризация не нужна.
 *
 * 🔴 Реплики приводятся к общему виду экрана — `{ channel, speaker, start,
 * end, text }`, как у файла и ссылки. Бэкенд отдаёт `speaker` = SPEAKER_LEFT /
 * SPEAKER_RIGHT и `speaker_role` = manager / client
 * (bit-dual-channel-v3/app/service.py:104), а экран результата, текст Истории
 * (`transcriptText`), .txt и выгрузка читают `speaker` и `channel`. Без
 * приведения в Истории и в файлах стояло «SPEAKER_LEFT» (ревью 02.10.2026).
 *
 * Исходные значения не теряются: роль остаётся в `speaker_role`, канал
 * бэкенда — в `channelSpeaker`. Будущая настройка именования каналов
 * переименовывает от них, а не от подписи. `result()` отдаёт ответ как есть.
 */

/** Подпись роли канала. Одна на весь фронт: её же пишет в Историю старый экран (App.js). */
export function stereoRoleLabel(role) {
  return role === 'manager' ? 'Менеджер' : 'Клиент';
}

/**
 * Сегменты bit-dual-channel-v3 → реплики экрана.
 *
 * Поддакивания («ага», «угу» короче 0,8 с — `is_backchannel` ставит сам сервис)
 * не показываются, как на старом экране стерео (DualChannelTranscription.js,
 * normalizeSegments). Новый экран их не отсеивал, и они шли на экран, в Историю
 * и в субтитры, накладываясь на соседние реплики (02.10.2026). Полный ответ
 * сервиса остаётся в `raw`.
 */
export function stereoFragments(segments) {
  return (segments || []).filter((s) => !s.is_backchannel).map((s) => ({
    channel: s.speaker === 'SPEAKER_RIGHT' ? 'R' : 'L',
    channelSpeaker: s.speaker,
    speaker: stereoRoleLabel(s.speaker_role),
    speaker_role: s.speaker_role,
    start: s.start,
    end: s.end,
    // Строкой: `null` в шаблоне `transcriptText` стал бы словом «null» в Истории.
    text: s.text == null ? '' : String(s.text),
  }));
}

async function start({ file }, { post, baseUrl, onUploadProgress }) {
  const body = new FormData();
  body.append('file', file);

  const response = await post(`${baseUrl}/start_dual_channel`, body, { onUploadProgress });
  return { taskId: response.data.task_id, baseUrl, kind: 'stereo' };
}

async function status({ taskId, baseUrl }, { get }) {
  const response = await get(`${baseUrl}/get_status?task_id=${taskId}`);
  return response.data;
}

async function result({ taskId, baseUrl }, { get }) {
  const response = await get(`${baseUrl}/get_json_result_v3?task_id=${taskId}`);
  return response.data;
}

/** Ждёт готовности и забирает реплики, разведённые по каналам.
 *
 * 🔴 `taskId` и `status` — как у файла (sources/file.js). По `taskId`
 * protocolHandoff привязывает Протокол к записи Истории; без него App заводил
 * рядом со стерео-записью вторую, типа «транскрибация» (аудит 02.10.2026).
 */
async function watch(task, deps) {
  const status = await pollUntilReady(task, deps);
  const data = await result(task, deps);
  return { fragments: stereoFragments(data.segments), raw: data, status, taskId: task.taskId };
}

/** Снятие задачи на сервере — как у файлового источника. */
async function cancel({ taskId, baseUrl }, { cancelTask }) {
  return cancelTask(baseUrl, taskId);
}

export const stereoSource = { key: 'stereo', start, status, result, watch, cancel };
